//
// Copyright (c) 2011-2017 by Curalate, Inc.
//

import $ from "jquery";

import _ from "js/underscore-mixins";

(function() {
    /**
     * This directive is used to dynamically bind and unbind watchers in a scope.
     * This is useful when one is attempting to load pages with large numbers of interactive items
     * The directive will watch all of the scopes within (but not including) the element it was attached to.
     * Usage:
     *  <div class="container" suspendable>
     *      <div class="item" ng-repeat="item in items"></div>
     *  </div>
     *  In the example above, all scopes generated inside of div.container will have their watchers removed if the
     *  div.item is scrolled/resized out of view
     */
  angular.module('curalate').directive('suspendable', [
    '$log',
    '$window',
    function(
            $log,
            $window,
        ) {
      return {
        link(scope, element, attrs) {
          const $jqWindow = $($window);
                    // When true, elements that have their watches suspended will be outlined in red while ones with active watchers will be outlined in green
          const DEBUG = attrs.suspendableDebug === 'true';
                    // Allow for custom find strings
          const findString = attrs.suspendableFinder || '.ng-scope, .ng-isolate-scope';
                    // When true, update suspended scopes on calls to $watch,
          const updateSuspended = attrs.suspendableUpdateSuspended === 'true';
          const uniqueSuspendableId = Math.random().toString(32).slice(2);
          const trackedEvents = [`scroll.suspendable-${uniqueSuspendableId}`, `resize.suspendable-${uniqueSuspendableId}`];
          const unbindHandlers = {};
          let heartbeat;

                    // Build and return an array of objects containing the scope information we need to process the watchers.
          function collectScopes(overrideFindString) {
            const finalFindString = overrideFindString || findString;

            return _.map(element.find(finalFindString), (elem) => {
              const angularElem = angular.element(elem);

              return {
                scope: angularElem.scope(),
                isolateScope: angularElem.isolateScope(),
                elem,
                angularElem,
              };
            });
          }

                // Allow for programmatic resuming of all suspendable scopes. This should be used sparingly, like in the case of
                // needed to batch update all scopes at once.
          scope.$on('suspendable-resume-suspended', (event, args) => {
            let scopes;

                    // If we have an ID to resume, use it. Otherwise resume all suspended scopes.
            if (args && args.suspendedId) {
              if (DEBUG) {
                $log.log('resuming scope', args.suspendedId);
              }

              scopes = collectScopes(`[data-scope-id="${args.suspendedId}"]`);
            } else {
              scopes = collectScopes();
            }

            if (DEBUG) {
              $log.log('resuming', scopes.length, 'scopes');
            }

            _.each(scopes, (obj) => {
              if (!obj.angularElem.attr('data-scope-id')) {
                obj.angularElem.attr('data-scope-id', _.uniqueId());
              }

              toggleWatchers(obj.scope, true);
              if (obj.isolateScope) {
                toggleWatchers(obj.isolateScope, true);
              }
            });
          });

                // Our main scope checking function
          function scopeCheckFunc(force, scopeId) {
            if (element.attr('suspendable-updates-paused') === 'true' && !force) {
              if (DEBUG) {
                $log.log('Updates paused');
              }
              return;
            }
            const windowOffset = $jqWindow.scrollTop();
            const windowHeight = $jqWindow.height();

            let scopes;
            if (scopeId) {
              scopes = collectScopes(`[data-scope-id="${scopeId}"]`);
            } else {
              scopes = collectScopes();
            }

            if (DEBUG) {
              $log.log('found', scopes.length, 'scopes for', findString, ' (scope id:', scopeId, ')');
            }

            _.each(scopes, (obj) => {
              const offset = obj.angularElem.offset();

              if (!obj.angularElem.attr('data-scope-id')) {
                obj.angularElem.attr('data-scope-id', _.uniqueId());
              }

                        // TODO this implementation is naive and there should be finer grained checks around an element's position vs page position
              const event = (offset.top <= windowOffset || offset.top >= windowOffset + windowHeight ||
                        !obj.angularElem.is(':visible')) ? 'suspend' : 'resume';

              toggleWatchers(obj.scope, event === 'resume');
              if (obj.isolateScope) {
                toggleWatchers(obj.isolateScope, event === 'resume');
              }
            });
          }

                // Prevent scope updates on heartbeat/scroll
          unbindHandlers['suspendable-pause-updates'] = scope.$on('suspendable-pause-updates', () => {
            if (DEBUG) {
              $log.log('Pausing updates');
            }
            element.attr('suspendable-updates-paused', true);
          });

                // Resume scope updates on heartbeat/scroll
          unbindHandlers['suspendable-resume-updates'] = scope.$on('suspendable-resume-updates', () => {
            if (DEBUG) {
              $log.log('Resuming updates');
            }
            element.attr('suspendable-updates-paused', false);
          });

                // If the scope gets destroyed, unbind the listeners we created
          scope.$on('$destroy', () => {
            $jqWindow.off(trackedEvents.join(' '));
            _.invoke(unbindHandlers, 'apply');
            clearInterval(heartbeat);
          });

                // Support calls to $watch when a scope is suspended. This will restore the watchers, execute the watch, then set the watchers to
                // null again.
          function watchDuringSuspend(...args) {
                    /* jshint validthis:true */
                    // If we haven't been suspended we will not have $$watchersBackup, so set it up here to be safe.
            this.$$watchersBackup = this.$$watchersBackup || [];
            this.$$watchers = this.$$watchersBackup;
            this.constructor.prototype.$watch.apply(this, args);
            this.$$watchers = null;
          }

                // Update watchers from the given scope down. If enable is true we "resume" the watchers, if false (or undefined) we
                // "suspend" the watchers.
          function toggleWatchers(scopeToToggle, enable) {
            let digest;
            let current;
            let next = scopeToToggle;

            while (next) {
              current = next;

              if (enable) {
                if (current.hasOwnProperty('$$watchersBackup')) {
                  current.$$watchers = current.$$watchersBackup;
                  delete current.$$watchersBackup;
                  delete current.$watch;
                                // Generally considered an anti-pattern, but we will trigger a $digest if we are not
                                // in the middle of one.
                  digest = !scopeToToggle.$root.$$phase;

                  if (DEBUG && current.css) {
                    current.css({
                      border: '1px solid green',
                    });
                  }
                }
              } else if (!current.hasOwnProperty('$$watchersBackup')) {
                current.$$watchersBackup = current.$$watchers;
                current.$$watchers = [];
                if (updateSuspended) {
                  current.$watch = watchDuringSuspend;
                }

                if (DEBUG && current.css) {
                  current.css({
                    border: '1px solid red',
                  });
                }
              }

                        // Perform a DFS to find child scopes
                        // NOTE: The use of double-$ methods here is not ideal but required to traverse the scope tree to find all
                        //       impacted scopes.
              next = current.$$childHead;
              while (!next && current !== scopeToToggle) {
                if (current.$$nextSibling) {
                  next = current.$$nextSibling;
                } else {
                  current = current.$parent;
                }
              }
            }

                    // Only trigger a digest if we are not in the middle of one.
            if (digest) {
              scopeToToggle.$digest();
            }
          }

                // Clean up after long/fast scrolls and reattach if hidden elements become visible
          heartbeat = setInterval(scopeCheckFunc, 2500);

                // Attach namespaced scroll and resize events to the window object. Only call the listener every 50ms
                // The listener will find all scopes within the container, attach a unique ID if necessary, and trigger
                // the appropriate event based on window scroll position and element offset.
          $jqWindow.on(trackedEvents.join(' '), _.debounce(() => { scopeCheckFunc(false); }, 50));
        },
      };
    }]);
})();
